import { ActionIcon, Group, Title } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";

import classes from "./CentredPage.module.css";
import { PageColumns } from "./PageColumns";

/** Renders the back link: the page passes its router Link, as `renderRoot` takes it. */
export type BackLink = (props: Record<string, unknown>) => ReactNode;

export interface CentredPageProps {
  /** The page's one h1, 20px, beside the back arrow. */
  title: string;
  back: BackLink;
  /** Beside the title: a status badge, an edit action. */
  titleRight?: ReactNode;
  /** At the far right of the header row. */
  right?: ReactNode;
  children: ReactNode;
}

/**
 * A page in a centered card of up to 1240px (design-system.md 3.8): the back arrow and the title
 * on top, the content scrolling below. Forms and detail pages use it (a brand, a job).
 */
export function CentredPage({ title, back, titleRight, right, children }: CentredPageProps) {
  const header = (
    <Group justify="space-between" wrap="nowrap" mb="md">
      <Group gap={8} wrap="nowrap" miw={0}>
        <ActionIcon variant="subtle" size="lg" aria-label="Back" renderRoot={back}>
          <ArrowLeft size={18} className={classes.backIcon} />
        </ActionIcon>
        <Title order={1} className={classes.title}>
          {title}
        </Title>
        {titleRight}
      </Group>
      {right}
    </Group>
  );
  return (
    <div className={classes.page}>
      <div className={classes.card}>
        {/* A form's right column ends at the card's edge, where the scrollbar would cover it. */}
        <PageColumns top={header} offsetScrollbar>
          {children}
        </PageColumns>
      </div>
    </div>
  );
}
